(function () {
  'use strict';

  const Rules = window.HadiyahDeliveryRules;
  if (!Rules || customElements.get('hadiyah-delivery-picker')) return;

  const PINCODE_STORAGE_KEY = 'hadiyah:pdp-pincode';
  const COUNTRY_STORAGE_KEY = 'hadiyah:delivery-country-code';

  class HadiyahDeliveryPicker extends HTMLElement {
    connectedCallback() {
      if (this.initialized) {
        this.startRuntimeObservers();
        this.bootstrapLocationGate();
        this.syncCardWidth();
        return;
      }
      this.form = document.getElementById(this.dataset.formId) || this.closest('form');
      if (!this.form) return;
      this.initialized = true;

      try {
        const payload = JSON.parse(this.querySelector('[data-hadiyah-delivery-data]')?.textContent || '{}');
        this.config = Rules.normalizeConfig(payload.config);
        this.product = payload.product || {};
      } catch (error) {
        this.config = Rules.normalizeConfig(null);
        this.product = {};
      }

      this.profile = Rules.resolveProfile(this.config, this.product);
      this.postcodeRule = Rules.resolvePostcodeRule(this.config, this.product);
      this.cacheElements();
      this.configurePropertyNames();
      this.bindEvents();
      this.configurePostcode();
      this.bootstrapLocationGate();
      this.clearAddToCartLocks();
      this.syncCardWidth();
      this.startRuntimeObservers();
      this.setAttribute('data-hadiyah-delivery-ready', '');

      if (this.config.timezoneWasCorrected) {
        console.warn('Hadiyah delivery picker: configuration timezone was reset to Asia/Kolkata.');
      }
    }

    disconnectedCallback() {
      window.clearInterval(this.refreshTimer);
      window.cancelAnimationFrame(this.controlsRefreshFrame);
      window.cancelAnimationFrame(this.cardWidthFrame);
      this.controlsObserver?.disconnect();
      this.form?.removeEventListener('submit', this.onSubmitCapture, true);
      document.removeEventListener('pointerdown', this.onDocumentPointerDown);
      document.removeEventListener('keydown', this.onDocumentKeyDown);
      document.removeEventListener('hadiyah:pincode:saved', this.onPincodeSaved);
      document.removeEventListener('hadiyah:delivery-country:saved', this.onDeliveryCountrySaved);
      window.removeEventListener('pageshow', this.onPageShow);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);
      window.removeEventListener('resize', this.onWindowResize);
    }

    startRuntimeObservers() {
      this.form.addEventListener('submit', this.onSubmitCapture, true);
      document.addEventListener('pointerdown', this.onDocumentPointerDown);
      document.addEventListener('keydown', this.onDocumentKeyDown);
      document.addEventListener('hadiyah:pincode:saved', this.onPincodeSaved);
      document.addEventListener('hadiyah:delivery-country:saved', this.onDeliveryCountrySaved);
      window.addEventListener('pageshow', this.onPageShow);
      document.addEventListener('visibilitychange', this.onVisibilityChange);
      window.addEventListener('resize', this.onWindowResize);
      window.clearInterval(this.refreshTimer);
      this.refreshTimer = window.setInterval(() => this.refreshForClockChange(), 60000);
      this.controlsObserver?.disconnect();
      this.controlsObserver = new MutationObserver(() => {
        window.cancelAnimationFrame(this.controlsRefreshFrame);
        this.controlsRefreshFrame = window.requestAnimationFrame(() => this.clearAddToCartLocks());
      });
      this.controlsObserver.observe(this.form, { childList: true, subtree: true });
    }

    cacheElements() {
      this.dateSection = this.querySelector('[data-hadiyah-delivery-date-section]');
      this.trigger = this.querySelector('[data-hadiyah-delivery-trigger]');
      this.dateText = this.querySelector('[data-hadiyah-delivery-date-text]');
      this.weekdayText = this.querySelector('[data-hadiyah-delivery-weekday]');
      this.calendar = this.querySelector('[data-hadiyah-delivery-calendar]');
      this.grid = this.querySelector('[data-hadiyah-delivery-grid]');
      this.monthText = this.querySelector('[data-hadiyah-delivery-month]');
      this.previousButton = this.querySelector('[data-hadiyah-delivery-previous]');
      this.nextButton = this.querySelector('[data-hadiyah-delivery-next]');
      this.timeField = this.querySelector('[data-hadiyah-delivery-time-field]');
      this.timeSelect = this.querySelector('[data-hadiyah-delivery-time]');
      this.postcodeBox = this.querySelector('[data-hadiyah-delivery-postcode]');
      this.indiaFields = this.querySelector('[data-hadiyah-delivery-india-fields]');
      this.summaryBox = this.querySelector('[data-hadiyah-delivery-summary]');
      this.summaryCountry = this.querySelector('[data-hadiyah-delivery-summary-country]');
      this.postcodeCompound = this.querySelector('[data-hadiyah-delivery-compound]');
      this.postcodeInput = this.querySelector('[data-hadiyah-delivery-postcode-input]');
      this.postcodeButton = this.querySelector('[data-hadiyah-delivery-postcode-check]');
      this.postcodeMessage = this.querySelector('[data-hadiyah-delivery-postcode-message]');
      this.availableBadge = this.querySelector('[data-hadiyah-delivery-available]');
      this.locationError = this.querySelector('[data-hadiyah-delivery-location-error]');
      this.dateError = this.querySelector('[data-hadiyah-delivery-date-error]');
      this.status = this.querySelector('[data-hadiyah-delivery-status]');
      this.error = this.querySelector('[data-hadiyah-delivery-error]');
      this.properties = Object.fromEntries([...this.querySelectorAll('[data-hadiyah-delivery-property]')]
        .map((input) => [input.dataset.hadiyahDeliveryProperty, input]));
    }

    configurePropertyNames() {
      Object.entries(this.properties).forEach(([key, input]) => {
        input.name = `properties[${this.config.propertyNames[key]}]`;
      });
    }

    bindEvents() {
      this.trigger.addEventListener('click', () => this.toggleCalendar());
      this.previousButton.addEventListener('click', () => this.changeMonth(-1));
      this.nextButton.addEventListener('click', () => this.changeMonth(1));
      this.grid.addEventListener('click', (event) => {
        const button = event.target.closest('[data-date]');
        if (button && !button.disabled) this.selectDate(button.dataset.date, true);
      });
      this.grid.addEventListener('keydown', (event) => this.onGridKeyDown(event));
      this.timeSelect.addEventListener('change', () => {
        this.selectedSlot = this.timeSelect.value;
        this.writeProperties();
      });
      this.postcodeInput.addEventListener('input', () => {
        const next = Rules.normalizePincode(this.postcodeInput.value);
        this.postcodeInput.value = next;
        if (this.postcodeResult?.valid && this.postcodeResult.pincode === next) return;
        this.postcodeResult = null;
        this.properties.pincode.disabled = true;
        this.properties.pincode.value = '';
        this.postcodeMessage.hidden = true;
        this.setAvailableState(false);
        this.clearLocationError();
        this.updateLocationGate({ preserveDate: false });
      });
      this.postcodeInput.addEventListener('keydown', (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          this.checkPostcode();
        }
      });
      this.postcodeButton.addEventListener('click', () => this.checkPostcode());

      this.onSubmitCapture = (event) => this.validateSubmission(event);
      this.onDocumentPointerDown = (event) => {
        if (!this.contains(event.target)) this.closeCalendar(false);
      };
      this.onDocumentKeyDown = (event) => {
        if (event.key === 'Escape' && !this.calendar.hidden) this.closeCalendar(true);
      };
      this.onPageShow = () => {
        this.bootstrapLocationGate();
        this.syncCardWidth();
      };
      this.onVisibilityChange = () => {
        if (!document.hidden) this.refreshForClockChange();
      };
      this.onWindowResize = () => {
        window.cancelAnimationFrame(this.cardWidthFrame);
        this.cardWidthFrame = window.requestAnimationFrame(() => this.syncCardWidth());
      };
      this.onPincodeSaved = (event) => {
        if (!this.needsPincode()) {
          this.bootstrapLocationGate();
          return;
        }

        const pincode = Rules.normalizePincode(event.detail?.pincode || '');
        if (!pincode) {
          this.postcodeInput.value = '';
          this.postcodeResult = null;
          this.setAvailableState(false);
          this.updateLocationGate({ preserveDate: false });
          return;
        }
        this.postcodeInput.value = pincode;
        this.checkPostcode({ announce: false, fromExternal: true });
      };
      this.onDeliveryCountrySaved = (event) => {
        const code = String(event?.detail?.code || this.readDeliveryCountryCode() || '').toUpperCase();
        const name = String(event?.detail?.name || '').trim();
        if (name && this.summaryCountry) this.summaryCountry.textContent = name;
        if (code) this.renderSummaryFlag(code);
        this.bootstrapLocationGate();
        this.syncCardWidth();
      };
    }

    syncCardWidth() {
      const host =
        this.closest('.buy-buttons-block') ||
        this.closest('product-form-component') ||
        this.form ||
        this;

      if (window.matchMedia('(max-width: 749px)').matches) {
        this.style.removeProperty('--hadiyah-delivery-end-inset');
        if (host !== this) host.style?.removeProperty?.('--hadiyah-delivery-end-inset');
        return;
      }

      const coins = document.querySelector('.pdp-price-hero__coins');
      const row = coins?.closest('.pdp-price-hero__row');
      if (!coins || !row) {
        this.style.removeProperty('--hadiyah-delivery-end-inset');
        if (host !== this) host.style?.removeProperty?.('--hadiyah-delivery-end-inset');
        return;
      }

      const rowRect = row.getBoundingClientRect();
      const coinsRect = coins.getBoundingClientRect();
      // Align card / ATC right edge with the left edge of the gift / HG Coins block.
      const inset = Math.round(rowRect.right - coinsRect.left);
      if (inset < 48 || inset > rowRect.width * 0.55) {
        this.style.removeProperty('--hadiyah-delivery-end-inset');
        if (host !== this) host.style?.removeProperty?.('--hadiyah-delivery-end-inset');
        return;
      }

      const value = `${inset}px`;
      this.style.setProperty('--hadiyah-delivery-end-inset', value);
      if (host !== this) host.style?.setProperty?.('--hadiyah-delivery-end-inset', value);
    }

    readDeliveryCountryCode() {
      try {
        const saved = String(localStorage.getItem(COUNTRY_STORAGE_KEY) || '')
          .trim()
          .toUpperCase();
        if (/^[A-Z]{2}$/.test(saved)) return saved;
      } catch (error) {
        /* Storage can be unavailable in privacy modes. */
      }

      const fromTrigger = this.querySelector('[data-hadiyah-delivery-code], [data-hadiyah-country-flag]')
        ?.dataset?.countryCode;
      if (fromTrigger && /^[A-Z]{2}$/i.test(String(fromTrigger).trim())) {
        return String(fromTrigger).trim().toUpperCase();
      }

      return 'IN';
    }

    readSavedPincode() {
      try {
        return Rules.normalizePincode(localStorage.getItem(PINCODE_STORAGE_KEY) || '');
      } catch (error) {
        return '';
      }
    }

    needsPincode() {
      return this.readDeliveryCountryCode() === 'IN';
    }

    isLocationReady() {
      if (!this.needsPincode()) return true;
      return Boolean(this.postcodeResult?.valid);
    }

    setAvailableState(isAvailable) {
      if (this.availableBadge) this.availableBadge.hidden = !isAvailable;
      this.postcodeCompound?.classList.toggle('is-available', Boolean(isAvailable));
    }

    jiggle(element) {
      if (!element) return;
      element.classList.remove('is-jiggle');
      // Force reflow so the animation can restart.
      void element.offsetWidth;
      element.classList.add('is-jiggle');
      window.setTimeout(() => element.classList.remove('is-jiggle'), 520);
    }

    setLocationError(message) {
      if (!this.locationError) return;
      this.locationError.textContent = message || 'Choose location first';
      this.locationError.hidden = false;
      this.postcodeCompound?.classList.add('is-invalid');
      this.summaryBox?.classList.add('is-invalid');
    }

    clearLocationError() {
      if (this.locationError) {
        this.locationError.hidden = true;
        this.locationError.textContent = '';
      }
      this.postcodeCompound?.classList.remove('is-invalid');
      this.summaryBox?.classList.remove('is-invalid');
    }

    setDateError(message) {
      if (!this.dateError) return;
      this.dateError.textContent = message || 'Please select a delivery date';
      this.dateError.hidden = false;
      this.trigger?.classList.add('is-invalid');
    }

    clearDateError() {
      if (this.dateError) {
        this.dateError.hidden = true;
        this.dateError.textContent = '';
      }
      this.trigger?.classList.remove('is-invalid');
    }

    clearDateSelection() {
      this.selectedDate = '';
      this.selectedSlot = '';
      if (this.dateText) this.dateText.textContent = 'Select delivery date';
      this.updateWeekdayLabel('');
      this.timeField.hidden = true;
      this.timeSelect.replaceChildren();
      this.writeProperties();
    }

    updateLocationGate({ preserveDate = true } = {}) {
      const ready = this.isLocationReady();
      if (this.dateSection) this.dateSection.hidden = !ready;

      if (!ready) {
        this.closeCalendar(false);
        if (!preserveDate) this.clearDateSelection();
        return;
      }

      this.clearLocationError();
      if (!this.selectedDate) this.prepareDatePicker();
      else this.writeProperties();
    }

    readDeliveryCountryName() {
      try {
        const saved = String(localStorage.getItem('hadiyah:delivery-country-name') || '').trim();
        if (saved) return saved;
      } catch (error) {
        /* no-op */
      }
      return this.summaryCountry?.textContent?.trim() || 'your location';
    }

    countryFlagUrl(countryCode) {
      const normalizedCode = String(countryCode || '').toUpperCase();
      if (!/^[A-Z]{2}$/.test(normalizedCode)) return '';

      const template =
        this.dataset?.hadiyahCountryFlagUrlTemplate ||
        document.querySelector('[data-hadiyah-delivery-modal]')?.dataset?.hadiyahCountryFlagUrlTemplate ||
        '';
      if (template) return template.replace('__country_code__', normalizedCode.toLowerCase());

      // Derive from any already-rendered flag src (e.g. clean-country-flag-in.svg → ae).
      const existingSrc =
        this.querySelector('[data-hadiyah-country-flag] img')?.getAttribute('src') || '';
      const match = existingSrc.match(/clean-country-flag-[a-z]{2}\.svg/i);
      if (match) {
        return existingSrc.replace(/clean-country-flag-[a-z]{2}\.svg/i, `clean-country-flag-${normalizedCode.toLowerCase()}.svg`);
      }
      return '';
    }

    renderSummaryFlag(countryCode) {
      const flag =
        this.summaryBox?.querySelector('[data-hadiyah-country-flag]') ||
        this.querySelector('.hadiyah-delivery__summary-flag');
      if (!flag) return;

      const normalizedCode = String(countryCode || this.readDeliveryCountryCode() || '').toUpperCase();
      if (!/^[A-Z]{2}$/.test(normalizedCode)) return;

      flag.dataset.countryCode = normalizedCode;
      const source = this.countryFlagUrl(normalizedCode);
      if (!source) {
        flag.textContent = normalizedCode;
        return;
      }

      let image = flag.querySelector('img');
      if (!image) {
        image = document.createElement('img');
        image.alt = '';
        image.width = 30;
        image.height = 22;
        image.decoding = 'async';
        flag.replaceChildren(image);
      }
      if (image.getAttribute('src') !== source) image.src = source;
    }

    syncSummaryLocation() {
      const code = this.readDeliveryCountryCode();
      const name = this.readDeliveryCountryName();
      if (this.summaryCountry) this.summaryCountry.textContent = name;
      this.renderSummaryFlag(code);
    }

    bootstrapLocationGate() {
      const intro = this.querySelector('.hadiyah-delivery__intro');
      if (this.postcodeBox) this.postcodeBox.hidden = false;

      if (!this.needsPincode()) {
        this.classList.add('is-international');
        if (intro) intro.textContent = 'Choose a delivery date for your order.';
        if (this.indiaFields) this.indiaFields.hidden = true;
        if (this.summaryBox) this.summaryBox.hidden = false;
        if (this.postcodeInput) {
          this.postcodeInput.hidden = true;
          this.postcodeInput.value = '';
          this.postcodeInput.setAttribute('tabindex', '-1');
        }
        if (this.postcodeButton) this.postcodeButton.hidden = true;
        if (this.availableBadge) this.availableBadge.hidden = true;
        this.postcodeResult = { valid: true, complete: true, pincode: '' };
        this.setAvailableState(false);
        this.clearLocationError();
        this.syncSummaryLocation();
        this.updateLocationGate({ preserveDate: true });
        return;
      }

      this.classList.remove('is-international');
      if (intro) intro.textContent = 'Enter your pincode to see available delivery options.';
      if (this.indiaFields) this.indiaFields.hidden = false;
      if (this.summaryBox) this.summaryBox.hidden = true;
      if (this.postcodeInput) {
        this.postcodeInput.hidden = false;
        this.postcodeInput.removeAttribute('tabindex');
      }
      if (this.postcodeButton) this.postcodeButton.hidden = false;
      if (this.postcodeResult?.valid && !this.postcodeResult.pincode) {
        this.postcodeResult = null;
      }

      const saved = this.readSavedPincode();
      if (saved.length === 6) {
        this.postcodeInput.value = saved;
        this.checkPostcode({ announce: false, fromExternal: true });
        return;
      }

      this.setAvailableState(false);
      this.updateLocationGate({ preserveDate: false });
    }

    prepareDatePicker() {
      if (!this.isLocationReady()) return;
      if (!this.profile.dateRule) {
        this.setError('Delivery dates are temporarily unavailable. Please contact us before ordering.');
        return;
      }

      const first = Rules.findFirstSelectableDate(this.profile.dateRule, this.profile.timeSlotRule);
      if (!first.key) {
        this.selectedDate = '';
        this.dateText.textContent = 'No delivery dates available';
        this.updateWeekdayLabel('');
        this.setError('No delivery date is currently available. Please contact us for help.');
        return;
      }

      // Default to the earliest selectable date so the field isn't empty.
      // Shoppers can still change it; date remains optional for Add to Cart.
      if (!this.selectedDate) {
        this.selectDate(first.key, false);
      } else {
        this.clearDateError();
        this.clearError();
      }
    }

    selectDate(key, announce) {
      if (!this.isLocationReady()) return;
      const dateRule = this.profile.dateRule;
      const window = Rules.getDateWindow(dateRule);
      if (!Rules.isSelectableDate(dateRule, key, window)) return;
      const slots = Rules.getAvailableSlots(this.profile.timeSlotRule, key);
      if (this.profile.timeSlotRule?.enabled && !slots.some((slot) => slot.available)) {
        this.setDateError('No delivery times remain for that date. Please choose another day.');
        this.jiggle(this.trigger);
        return;
      }
      const dateChanged = key !== this.selectedDate;
      this.selectedDate = key;
      const selected = Rules.dateFromKey(key);
      this.viewYear = selected.getUTCFullYear();
      this.viewMonth = selected.getUTCMonth();
      this.dateText.textContent = Rules.formatDate(key);
      this.updateWeekdayLabel(key);
      this.clearError();
      this.clearDateError();
      this.renderTimeSlots(slots, !dateChanged);
      this.renderCalendar();
      this.writeProperties();
      if (announce) {
        // Weekday line already confirms the selection — don't repeat it in status.
        this.setStatus('');
        this.closeCalendar(true);
      }
    }

    renderTimeSlots(slots, preserveSelection = false) {
      const rule = this.profile.timeSlotRule;
      if (!rule?.enabled) {
        this.timeField.hidden = true;
        this.timeSelect.replaceChildren();
        this.selectedSlot = '';
        return;
      }
      this.timeField.hidden = false;
      const previous = this.selectedSlot;
      this.timeSelect.replaceChildren();
      slots.forEach((slot) => {
        if (!slot.available && !rule.showUnavailableSlots) return;
        const option = document.createElement('option');
        option.value = slot.label;
        option.textContent = slot.available ? slot.label : `${slot.label} — unavailable`;
        option.disabled = !slot.available;
        this.timeSelect.appendChild(option);
      });
      const available = slots.filter((slot) => slot.available);
      const restored = preserveSelection ? available.find((slot) => slot.label === previous) : null;
      this.selectedSlot = restored?.label || (rule.autoSelectFirst ? Rules.firstAvailableSlot(slots)?.label : '') || '';
      this.timeSelect.value = this.selectedSlot;
    }

    updateWeekdayLabel(key) {
      if (!this.weekdayText) return;
      if (!key) {
        this.weekdayText.textContent = '';
        this.weekdayText.hidden = true;
        return;
      }
      this.weekdayText.textContent = `${Rules.formatLongDate(key)} · selected for delivery`;
      this.weekdayText.hidden = false;
    }

    writeProperties() {
      const ready = this.isLocationReady();
      const hasDate = Boolean(ready && this.selectedDate);
      this.properties.date.value = hasDate ? Rules.formatDate(this.selectedDate) : '';
      this.properties.date.disabled = !hasDate;
      this.properties.day.value = hasDate ? Rules.formatDay(this.selectedDate) : '';
      this.properties.day.disabled = !hasDate;
      const hasTime = Boolean(ready && this.profile.timeSlotRule?.enabled && this.selectedSlot);
      this.properties.time.value = hasTime ? this.selectedSlot : '';
      this.properties.time.disabled = !hasTime;
      const hasPincode = Boolean(this.needsPincode() && this.postcodeResult?.valid);
      this.properties.pincode.value = hasPincode ? this.postcodeResult.pincode : '';
      this.properties.pincode.disabled = !hasPincode;
    }

    toggleCalendar() {
      if (!this.isLocationReady()) return;
      if (this.calendar.hidden) this.openCalendar();
      else this.closeCalendar(false);
    }

    openCalendar() {
      this.calendar.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
      this.renderCalendar();
      requestAnimationFrame(() => {
        (this.grid.querySelector('[aria-selected="true"]:not(:disabled)')
          || this.grid.querySelector('button:not(:disabled)'))?.focus();
      });
    }

    closeCalendar(restoreFocus) {
      if (this.calendar.hidden) return;
      this.calendar.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.trigger.focus();
    }

    changeMonth(amount) {
      const date = new Date(Date.UTC(this.viewYear, this.viewMonth + amount, 1, 12));
      const targetMonth = `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, '0')}`;
      const window = Rules.getDateWindow(this.profile.dateRule);
      if (targetMonth < window.earliestKey.slice(0, 7) || targetMonth > window.maximumKey.slice(0, 7)) return;
      this.viewYear = date.getUTCFullYear();
      this.viewMonth = date.getUTCMonth();
      this.renderCalendar();
      requestAnimationFrame(() => this.grid.querySelector('button:not(:disabled)')?.focus());
    }

    renderCalendar() {
      if (!Number.isInteger(this.viewYear)) return;
      const window = Rules.getDateWindow(this.profile.dateRule);
      const firstOfMonth = new Date(Date.UTC(this.viewYear, this.viewMonth, 1, 12));
      const firstWeekday = firstOfMonth.getUTCDay();
      const daysInMonth = new Date(Date.UTC(this.viewYear, this.viewMonth + 1, 0, 12)).getUTCDate();
      this.monthText.textContent = `${Rules.MONTH_NAMES[this.viewMonth]} ${this.viewYear}`;
      this.grid.replaceChildren();
      for (let blank = 0; blank < firstWeekday; blank += 1) {
        const spacer = document.createElement('span');
        spacer.className = 'hadiyah-delivery__day-spacer';
        spacer.setAttribute('role', 'presentation');
        this.grid.appendChild(spacer);
      }
      for (let day = 1; day <= daysInMonth; day += 1) {
        const key = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
        let selectable = Rules.isSelectableDate(this.profile.dateRule, key, window);
        if (selectable && this.profile.timeSlotRule?.enabled) {
          selectable = Rules.getAvailableSlots(this.profile.timeSlotRule, key).some((slot) => slot.available);
        }
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'hadiyah-delivery__day';
        button.dataset.date = key;
        button.textContent = String(day);
        button.setAttribute('role', 'gridcell');
        button.setAttribute('aria-label', `${Rules.formatDay(key)}, ${Rules.formatDate(key)}`);
        button.setAttribute('aria-selected', String(key === this.selectedDate));
        button.disabled = !selectable;
        button.setAttribute('aria-disabled', String(!selectable));
        button.tabIndex = key === this.selectedDate ? 0 : -1;
        this.grid.appendChild(button);
      }
      const monthStart = `${this.viewYear}-${String(this.viewMonth + 1).padStart(2, '0')}-01`;
      this.previousButton.disabled = monthStart.slice(0, 7) <= window.earliestKey.slice(0, 7);
      this.nextButton.disabled = monthStart.slice(0, 7) >= window.maximumKey.slice(0, 7);
    }

    onGridKeyDown(event) {
      const current = event.target.closest('[data-date]');
      if (!current) return;
      const offsets = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7 };
      let targetKey;
      if (event.key in offsets) {
        const step = offsets[event.key];
        targetKey = Rules.addDays(current.dataset.date, step);
        const window = Rules.getDateWindow(this.profile.dateRule);
        let found = false;
        for (let attempts = 0; attempts < 60; attempts += 1) {
          const selectable = Rules.isSelectableDate(this.profile.dateRule, targetKey, window)
            && (!this.profile.timeSlotRule?.enabled
              || Rules.getAvailableSlots(this.profile.timeSlotRule, targetKey).some((slot) => slot.available));
          if (selectable) { found = true; break; }
          targetKey = Rules.addDays(targetKey, step);
        }
        if (!found) return;
      }
      else if (event.key === 'Home') targetKey = Rules.addDays(current.dataset.date, -Rules.weekday(current.dataset.date));
      else if (event.key === 'End') targetKey = Rules.addDays(current.dataset.date, 6 - Rules.weekday(current.dataset.date));
      else if (event.key === 'PageUp' || event.key === 'PageDown') {
        event.preventDefault();
        this.changeMonth(event.key === 'PageUp' ? -1 : 1);
        return;
      } else return;
      event.preventDefault();
      const targetDate = new Date(`${targetKey}T12:00:00Z`);
      if (targetDate.getUTCMonth() !== this.viewMonth || targetDate.getUTCFullYear() !== this.viewYear) {
        this.viewMonth = targetDate.getUTCMonth();
        this.viewYear = targetDate.getUTCFullYear();
        this.renderCalendar();
      }
      this.grid.querySelector(`[data-date="${targetKey}"]:not(:disabled)`)?.focus();
    }

    configurePostcode() {
      this.postcodeInput.placeholder = this.config.postcode.placeholder || 'Enter pincode';
    }

    checkPostcode({ announce = true, fromExternal = false } = {}) {
      const pincode = Rules.normalizePincode(this.postcodeInput.value);
      this.postcodeInput.value = pincode;

      if (this.postcodeRule) {
        this.postcodeResult = Rules.validatePincode(this.config, this.postcodeRule, pincode);
      } else {
        const complete = pincode.length === 6;
        this.postcodeResult = {
          valid: complete,
          complete,
          pincode,
          message: complete
            ? (this.config.postcode.availableMessage || 'Delivery is available for this pincode.')
            : 'Enter a complete six-digit pincode.',
        };
      }

      const isValid = Boolean(this.postcodeResult.valid);
      this.setAvailableState(isValid);

      if (!isValid) {
        this.postcodeMessage.hidden = false;
        this.postcodeMessage.classList.add('is-error');
        this.postcodeMessage.classList.remove('is-success');
        this.postcodeMessage.textContent = this.postcodeResult.complete
          ? (this.postcodeResult.message || 'Delivery is not available for this pincode.')
          : 'Enter a complete six-digit pincode.';
      } else {
        this.postcodeMessage.hidden = true;
        this.postcodeMessage.textContent = '';
        this.postcodeMessage.classList.remove('is-error', 'is-success');
        try {
          localStorage.setItem(PINCODE_STORAGE_KEY, this.postcodeResult.pincode);
        } catch (error) { /* Storage can be unavailable in privacy modes. */ }
        if (announce && !fromExternal) {
          this.setStatus('Delivery is available for this pincode.');
        }
      }

      this.writeProperties();
      this.updateLocationGate({ preserveDate: isValid });
      if (isValid) this.clearLocationError();
    }

    /**
     * Hard requirements that must pass before Add to Cart / Customize.
     * Delivery date is optional — missing date must not block the cart.
     * If a date is chosen and a time slot is required, the slot is still enforced.
     */
    isHardComplete() {
      if (!this.isLocationReady()) return false;
      if (this.needsPincode() && !this.postcodeResult?.valid) return false;
      if (
        this.selectedDate &&
        this.profile.timeSlotRule?.enabled &&
        this.profile.timeSlotRule.required &&
        !this.selectedSlot
      ) {
        return false;
      }
      return true;
    }

    /** Full schedule filled (location + optional date + slot). Used for UI completeness only. */
    isComplete() {
      if (!this.isHardComplete()) return false;
      if (!this.selectedDate) return false;
      return true;
    }

    clearAddToCartLocks() {
      if (!this.form) return;
      const buttons = [
        ...this.form.querySelectorAll('[type="submit"][name="add"], .add-to-cart-button'),
        ...document.querySelectorAll(`[form="${CSS.escape(this.form.id)}"][type="submit"]`),
      ];
      buttons.forEach((button) => {
        if (!button.hasAttribute('data-hadiyah-delivery-disabled')) return;
        if (
          button.dataset.themeCartAddLock === 'true' ||
          this.form.dataset.themeCartAddInFlight === 'true'
        ) {
          button.setAttribute('aria-disabled', 'true');
          button.setAttribute('aria-busy', 'true');
          button.removeAttribute('data-hadiyah-delivery-disabled');
          delete button.dataset.hadiyahDeliveryPreviousAriaDisabled;
          return;
        }
        const previous = button.dataset.hadiyahDeliveryPreviousAriaDisabled;
        if (previous) button.setAttribute('aria-disabled', previous);
        else button.removeAttribute('aria-disabled');
        delete button.dataset.hadiyahDeliveryPreviousAriaDisabled;
        button.removeAttribute('data-hadiyah-delivery-disabled');
      });

      this.form.querySelectorAll('.accelerated-checkout-block').forEach((container) => {
        if (!container.hasAttribute('data-hadiyah-delivery-disabled')) return;
        container.hidden = false;
        container.removeAttribute('data-hadiyah-delivery-disabled');
      });
    }

    validateSubmission(event) {
      this.refreshForClockChange();
      if (this.isHardComplete()) {
        this.clearLocationError();
        this.clearDateError();
        this.clearError();
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      this.blockIncompleteAction();
    }

    blockIncompleteAction() {
      const locationMissing = this.needsPincode() && !this.postcodeResult?.valid;
      if (locationMissing) {
        this.clearDateError();
        this.setLocationError('Choose location first');
        this.jiggle(this.postcodeCompound);
        this.postcodeInput?.focus();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      if (
        this.selectedDate &&
        this.profile.timeSlotRule?.enabled &&
        this.profile.timeSlotRule.required &&
        !this.selectedSlot
      ) {
        this.clearLocationError();
        this.setDateError('Please select a delivery time');
        this.jiggle(this.timeSelect);
        this.timeSelect?.focus();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return false;
      }

      // Delivery date is optional — never hard-block Add to Cart for a missing date.
      return true;
    }

    refreshForClockChange() {
      if (!this.isLocationReady() || !this.selectedDate || !this.profile.dateRule) return;
      const window = Rules.getDateWindow(this.profile.dateRule);
      const slots = Rules.getAvailableSlots(this.profile.timeSlotRule, this.selectedDate);
      const dateStillValid = Rules.isSelectableDate(this.profile.dateRule, this.selectedDate, window);
      const slotStillValid = !this.profile.timeSlotRule?.enabled
        || slots.some((slot) => slot.available && slot.label === this.selectedSlot);
      if (!dateStillValid || !slotStillValid) {
        this.clearDateSelection();
        this.prepareDatePicker();
        this.setStatus('Availability changed while this page was open. Please select a delivery date again.');
      } else {
        this.renderTimeSlots(slots, true);
        this.writeProperties();
      }
    }

    setStatus(message) { this.status.textContent = message; }
    setError(message) { this.error.textContent = message; this.error.hidden = false; }
    clearError() { this.error.textContent = ''; this.error.hidden = true; }
  }

  customElements.define('hadiyah-delivery-picker', HadiyahDeliveryPicker);

  function findActiveDeliveryPicker() {
    return document.querySelector('hadiyah-delivery-picker[data-hadiyah-delivery-ready]');
  }

  function isCustomizeOrBuyTrigger(target) {
    if (!target || !(target instanceof Element)) return null;
    return (
      target.closest('.product-form__buttons [name="add"]') ||
      target.closest('sticky-atc [name="add"], sticky-atc .product-form__submit, sticky-atc .add-to-cart-button') ||
      target.closest(
        '[data-type="customeowApp"] button, [data-type="customeowApp"] a, [data-type="customeowApp"] [role="button"], [data-type="customeowApp"] input[type="button"], [data-type="customeowApp"] input[type="submit"]',
      )
    );
  }

  function gateCustomizeAccess(event) {
    const trigger = isCustomizeOrBuyTrigger(event.target);
    if (!trigger) return true;

    const picker = findActiveDeliveryPicker();
    if (!picker || typeof picker.isHardComplete !== 'function') return true;
    if (picker.isHardComplete()) return true;

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    picker.blockIncompleteAction();
    return false;
  }

  window.HadiyahDeliveryGate = {
    allow(event) {
      const picker = findActiveDeliveryPicker();
      if (!picker || typeof picker.isHardComplete !== 'function') return true;
      if (picker.isHardComplete()) return true;
      if (event) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
      }
      picker.blockIncompleteAction();
      return false;
    },
  };

  // Capture before Customeow / theme Customize Now handlers so incomplete delivery cannot open the editor.
  document.addEventListener('click', gateCustomizeAccess, true);
  document.addEventListener('pointerdown', gateCustomizeAccess, true);
})();
